import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

/* Status-bar glyphs drawn to match the real ones: the react-icons set has no
   four-bar signal or an outlined battery with a fill level. All decorative. */

export function SignalGlyph() {
    return (
        <svg width="17" height="11" viewBox="0 0 17 11" aria-hidden="true">
            <rect x="0" y="7" width="3" height="4" rx="1" fill="currentColor" />
            <rect x="4.5" y="5" width="3" height="6" rx="1" fill="currentColor" />
            <rect x="9" y="2.5" width="3" height="8.5" rx="1" fill="currentColor" />
            <rect x="13.5" y="0" width="3" height="11" rx="1" fill="currentColor" />
        </svg>
    );
}

export function WifiGlyph() {
    return (
        <svg width="15" height="11" viewBox="0 0 15 11" aria-hidden="true">
            <path
                fill="currentColor"
                d="M7.5 2.2c2.1 0 4 .8 5.4 2.1l1.1-1.1A9.2 9.2 0 0 0 7.5.6 9.2 9.2 0 0 0 1 3.2l1.1 1.1a7.7 7.7 0 0 1 5.4-2.1zm0 3.1c1.2 0 2.4.5 3.2 1.3l1.1-1.1a6.2 6.2 0 0 0-8.6 0l1.1 1.1a4.6 4.6 0 0 1 3.2-1.3zm0 3.1c.4 0 .8.2 1.1.4L7.5 10 6.4 8.8c.3-.2.7-.4 1.1-.4z"
            />
        </svg>
    );
}

/** `level` is the filled fraction, 0-1. */
export function BatteryGlyph({ level = 0.8 }: { level?: number }) {
    return (
        <svg width="25" height="12" viewBox="0 0 26 12" aria-hidden="true">
            <rect x=".5" y=".5" width="22" height="11" rx="3.5" fill="none" stroke="currentColor" strokeOpacity=".45" />
            <rect x="2" y="2" width={19 * level} height="8" rx="2" fill="currentColor" />
            <path d="M24 4v4c.8-.3 1.3-1.1 1.3-2s-.5-1.7-1.3-2z" fill="currentColor" fillOpacity=".45" />
        </svg>
    );
}

export function ControlCentreGlyph() {
    return (
        <svg width="15" height="13" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="1" y="1" width="22" height="8" rx="4" />
            <circle cx="18" cy="5" r="2" fill="currentColor" />
            <rect x="1" y="11" width="22" height="8" rx="4" />
            <circle cx="6" cy="15" r="2" fill="currentColor" />
        </svg>
    );
}

/**
 * Lays its children out on a fixed logical canvas (`width` x `height` CSS px)
 * and scales that canvas to the container's width. The device screens are
 * designed at one size, like the mock-ups, so every layout, font and icon keeps
 * its proportions on any viewport instead of reflowing.
 */
export function ScaledStage({
    width,
    height,
    className,
    children,
}: {
    width: number;
    height: number;
    className?: string;
    children: ReactNode;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => {
            if (el.clientWidth > 0) setScale(el.clientWidth / width);
        };
        update();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [width]);

    return (
        <div ref={ref} className={`device-stage${className ? ` ${className}` : ""}`} style={{ aspectRatio: `${width} / ${height}` }}>
            <div className="device-stage-canvas" style={{ width, height, transform: `scale(${scale})` }}>
                {children}
            </div>
        </div>
    );
}
